/**
 * Parses the input data into a table array format.
 * @param {*} data - The input data (array, DenseMatrix, or JSON string).
 * @returns {Array} - Parsed table data as an array.
 * @throws {Error} - If data format is invalid.
 */
function parseTableData(data) {
  // Check if data is a DenseMatrix
  if (data && typeof data === "object" && data._data && Array.isArray(data._data)) {
    return data._data;
  }

  if (typeof data === "string") {
    try {
      return JSON.tryParse(data);
    } catch (error) {
      console.log("Error parsing JSON:", error);
      throw new Error("Invalid JSON format for table data");
    }
  }

  if (Array.isArray(data)) {
    return data;
  }

  throw new Error("Input must be an array, a DenseMatrix, or a JSON string representing an array");
}

/**
 * Parses a field value into an object.
 * @param {*} fieldValue - The field value (string or object).
 * @param {string} column - The column name for error reporting.
 * @returns {Object} - Parsed field object.
 * @throws {Error} - If field format is invalid.
 */
function parseFieldObject(fieldValue, column) {
  if (typeof fieldValue === "string") {
    try {
      return JSON.tryParse(fieldValue);
    } catch (error) {
      console.log("Error parsing JSON in field:", error);
      throw new Error(`Invalid JSON format in field for column "${column}"`);
    }
  }

  if (typeof fieldValue === "object" && fieldValue !== null) {
    return fieldValue;
  }

  throw new Error(`Field in column "${column}" must be either a JSON string or an object`);
}

/**
 * Validates that a field object has required href and text properties.
 * @param {Object} fieldObj - The field object to validate.
 * @param {string} column - The column name for error reporting.
 * @throws {Error} - If required properties are missing.
 */
function validateFieldObject(fieldObj, column) {
  if (!fieldObj.href && !fieldObj.text) {
    throw new Error(
      `Both 'href' and 'text' properties are missing in the field object for column "${column}"`
    );
  }

  if (!fieldObj.href) {
    throw new Error(`Missing 'href' property in the field object for column "${column}"`);
  }

  if (!fieldObj.text) {
    throw new Error(`Missing 'text' property in the field object for column "${column}"`);
  }
}

export function getHyperlinksFromFieldInTable(data, column) {
  const tableData = parseTableData(data);

  // Ensure tableData is an array
  if (!Array.isArray(tableData)) {
    throw new Error("Processed input must be an array");
  }

  const links = [];
  for (const row of tableData) {
    const fieldObj = parseFieldObject(row[column], column);
    validateFieldObject(fieldObj, column);
    links.push(`<a href="${fieldObj.href}" target="_blank">${fieldObj.text}</a>`);
  }

  return links.join("</br>");
}
